import { useState, useEffect, useRef } from 'react';

/**
 * useDragSort — drag handle only approach.
 * draggable is on the HANDLE element only, not the whole row.
 * This prevents drag from intercepting clicks on checkboxes/buttons.
 *
 * Returns:
 *   items      — ordered array
 *   rowProps   — spread on the row container (onDragOver, onDrop, onDragLeave)
 *   handleProps— spread on the drag handle element only (draggable, onDragStart, onDragEnd)
 */
export function useDragSort(sourceItems, onReorder) {
  const [items, setItems] = useState(sourceItems ?? []);

  const isDragging = useRef(false);

  // Sync from source only when not mid-drag
  useEffect(() => {
    if (isDragging.current) return;
    if (!sourceItems) return;
    setItems(prev => {
      const srcIds = sourceItems.map(i => i.id).join(',');
      const curIds = prev.map(i => i.id).join(',');
      if (srcIds !== curIds) {
        // IDs changed (add/delete) — reset to source order
        return sourceItems;
      }
      // IDs same but DATA may have changed (e.g. next_due_date after recurring complete).
      // Keep current ORDER (drag order) but update each item's data from source.
      const srcMap = new Map(sourceItems.map(i => [i.id, i]));
      return prev.map(item => srcMap.get(item.id) ?? item);
    });
  }, [sourceItems]);

  const dragId = useRef(null);
  const overId = useRef(null);

  // Props for the HANDLE element (the 6-dot grip icon)
  const handleProps = (id) => ({
    draggable: true,

    onDragStart(e) {
      isDragging.current = true;
      dragId.current     = id;
      overId.current     = id;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(id));
      // Mark the parent row via data attribute — CSS does the visual
      const row = e.currentTarget.closest('[data-drag-row]');
      if (row) row.setAttribute('data-dragging', 'true');
    },

    onDragEnd(e) {
      const row = e.currentTarget.closest('[data-drag-row]');
      if (row) row.removeAttribute('data-dragging');
      document.querySelectorAll('[data-dragover]')
        .forEach(el => el.removeAttribute('data-dragover'));

      // Finalize order on drag end (works even if onDrop didn't fire)
      const fromId = dragId.current;
      const toId   = overId.current;
      isDragging.current = false;
      dragId.current     = null;
      overId.current     = null;

      if (!fromId || !toId || fromId === toId) return;

      setItems(prev => {
        const from = prev.findIndex(it => it.id === fromId);
        const to   = prev.findIndex(it => it.id === toId);
        if (from < 0 || to < 0 || from === to) return prev;
        const next = [...prev];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        if (onReorder) onReorder(next);
        return next;
      });
    },
  });

  // Props for the ROW container (tracks hover target, no draggable attr)
  const rowProps = (id) => ({
    'data-drag-row': id,

    onDragOver(e) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (overId.current !== id) {
        document.querySelectorAll('[data-dragover]')
          .forEach(el => el.removeAttribute('data-dragover'));
        overId.current = id;
        if (id !== dragId.current) {
          e.currentTarget.setAttribute('data-dragover', 'true');
        }
      }
    },

    onDragLeave(e) {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        e.currentTarget.removeAttribute('data-dragover');
      }
    },

    onDrop(e) {
      e.preventDefault();
      e.currentTarget.removeAttribute('data-dragover');
      // onDragEnd will finalize — nothing needed here
    },
  });

  return { items, rowProps, handleProps };
}